/**
 * pairingRule.api.ts — the 商品搭配規則 transport seat (Feat #2168, the #2167
 * mock-first contract).
 *
 * WHY THIS MODULE EXISTS AT ALL (2026-09-16, the union with develop v2026.9.1).
 * The pairing-rule hooks were written against `apiClient` directly, with a
 * docblock recording the promotion into the service layer as a followUp. The
 * products-surface census (`productSurfaceCensus.test.ts`) counts every
 * transport call site in the router's import closure, and `usePairingRules.ts`
 * is in that closure: four sites outside `src/services/api/` with no row in the
 * bypass register, which is the class the census exists to fail. The remedy it
 * names first is ROUTING, and routing is what this file is — the followUp
 * discharged, not a register row that would make the count green while the
 * call sites stayed where they were.
 *
 * NO LIVE LEG, AND THAT IS NOT A HOLE IN THE §0g SENSE. There is no `pairing`
 * root, type or operation document anywhere in the frozen contract
 * (`packages/contracts/schema.graphql`: zero occurrences) — the whole feature
 * is REST on both seams, READ and WRITE, in every posture. So this facade never
 * consults `isLiveModule`: a flip switch with nothing to switch to would be a
 * lie, and a `refuse…()` here would refuse a write that its own read can see.
 * That is the property `liveFacadeWriteParity.test.ts` §D turns on — a REST
 * write is unobservable when the READ of the same rows is graph-served, and
 * these rows are not.
 *
 * REMOVAL TRIGGER: a pairing-rule read model on the contract — then these four
 * methods grow live legs exactly as `productApi.list` did, and every consumer
 * keeps its signature.
 */
import apiClient from './axios.config';
import { API_ROUTES } from '@/constants/api.constants';
import type {
  CreateProductPairingRuleRequest,
  ProductPairingRule,
  UpdateProductPairingRuleRequest,
} from '@/types/api/pairingRule';
import type {
  NetSuiteAvailabilityResponse,
  NetSuiteAvailabilityRow,
} from '@/types/api/purchaseOrder';

const pairingRulesOf = (productId: string) => `${API_ROUTES.PRODUCTS}/${productId}/pairing-rules`;

export const pairingRuleApi = {
  /**
   * GET /api/products/:productId/pairing-rules
   * ACTIVE AND INACTIVE — a retired rule stays listed (there is no DELETE);
   * the filtering is the UI's, so the transport never hides a row.
   */
  list: async (productId: string): Promise<ProductPairingRule[]> => {
    const response = await apiClient.get<{ data: ProductPairingRule[] }>(
      pairingRulesOf(productId)
    );
    return response.data.data;
  },

  /** POST /api/products/:productId/pairing-rules */
  create: async (
    productId: string,
    body: CreateProductPairingRuleRequest
  ): Promise<ProductPairingRule> => {
    const response = await apiClient.post<{ data: ProductPairingRule }>(
      pairingRulesOf(productId),
      body
    );
    return response.data.data;
  },

  /**
   * PATCH /api/pairing-rules/:ruleId — the whole lifecycle, retirement
   * included: the contract carries no DELETE, so deactivation is a PATCH of
   * `isActive` and the row stays readable.
   */
  update: async (
    ruleId: string,
    patch: UpdateProductPairingRuleRequest
  ): Promise<ProductPairingRule> => {
    const response = await apiClient.patch<{ data: ProductPairingRule }>(
      `/pairing-rules/${ruleId}`,
      patch
    );
    return response.data.data;
  },

  /**
   * GET /api/netsuite/availability?productIds=… — the batched Tier-1 echo the
   * pairing indicator reads for its COMPONENT products.
   *
   * The ERP web never owns on-hand (Fixed decision 3), so this is a window onto
   * NetSuite rather than a product read, and it is the same two-tier boundary
   * `useItemLocations` sits on. It is routed rather than registered for the
   * reason the census gives: a call site inside the seam is counted by the
   * seam's own instruments, and a registered one is counted only for as long as
   * somebody keeps the row honest.
   *
   * Absent data stays absent — the caller renders NO indicator rather than a
   * fabricated quantity.
   */
  componentAvailability: async (productIds: string): Promise<NetSuiteAvailabilityRow[]> => {
    const response = await apiClient.get<NetSuiteAvailabilityResponse>('/netsuite/availability', {
      params: { productIds },
    });
    return response.data.data;
  },
};

export default pairingRuleApi;
